import { useEffect, useMemo, useState } from 'react'
import { ShieldQuestion } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { useToast } from '@/providers/UiProviders'
import { GeoRegistryTable } from './GeoRegistryTable'
import { GEO_STR as S, fetchVpnDetection, setVpnDetection, type GeoConfigRow } from './geoApi'

/**
 * A11.14 — VPN/proxy aniqlash. Admin VPN/proxy/datacenter diapazonlarini va mos
 * kelganda amalni (block/flag/allow) boshqaradi + aniqlash-yoqilgan tumbleri
 * (app_settings). JONLI CRUD: `/admin/geo/vpn-ranges` + `/admin/geo/vpn-config`.
 */
function VpnDetectionToggle() {
  const { toast } = useToast()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    fetchVpnDetection(ac.signal).then(setEnabled)
    return () => ac.abort()
  }, [])

  const toggle = async () => {
    if (enabled == null || busy) return
    setBusy(true)
    const next = !enabled
    try {
      await setVpnDetection(next)
      setEnabled(next)
      toast(S.detectionSaved, { tone: 'success' })
    } catch {
      toast(S.opFailed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const on = enabled === true
  return (
    <div className="mx-4 mt-3 flex items-center justify-between rounded-lg border border-border-default bg-surface-2 px-3 py-2.5">
      <span className="text-sm font-medium text-primary">{S.vpnDetectionLabel}</span>
      <button
        type="button"
        onClick={toggle}
        disabled={enabled == null || busy}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
          on
            ? 'border-success bg-success-subtle text-success'
            : 'border-border-default bg-surface-1 text-secondary'
        }`}
      >
        <span className={`h-2 w-2 rounded-full ${on ? 'bg-success' : 'bg-muted'}`} />
        {enabled == null ? '…' : on ? S.enabledOn : S.enabledOff}
      </button>
    </div>
  )
}

export function VpnProxyScreen() {
  const columns = useMemo<AdminColumn<GeoConfigRow>[]>(
    () => [
      {
        key: 'cidr',
        header: S.cidr,
        type: 'text',
        minWidth: 180,
        render: (r) => <span className="font-mono tabular text-primary">{String(r.cidr ?? '')}</span>,
      },
      { key: 'provider', header: S.provider, type: 'text', minWidth: 160 },
      {
        key: 'action',
        header: S.actionCol,
        type: 'enum',
        width: 140,
        options: [
          { value: 'block', label: S.actBlock, status: 'banned' },
          { value: 'flag', label: S.actFlag, status: 'review' },
          { value: 'allow', label: S.actAllow, status: 'active' },
        ],
      },
      { key: 'created_at', header: S.createdCol, type: 'date', width: 150 },
    ],
    [],
  )

  return (
    <GeoRegistryTable
      screenId="A11.14"
      title={S.vpnTitle}
      description={S.vpnNote}
      addLabel={S.addVpnRange}
      path="/admin/geo/vpn-ranges"
      listKey="ranges"
      searchKeys={['cidr', 'provider', 'action']}
      fields={[
        { name: 'cidr', placeholder: S.cidr, mono: true },
        { name: 'provider', placeholder: S.provider },
        {
          name: 'action',
          placeholder: S.actionCol,
          options: [
            { value: 'flag', label: S.actFlag },
            { value: 'block', label: S.actBlock },
            { value: 'allow', label: S.actAllow },
          ],
        },
      ]}
      columns={columns}
      icon={<ShieldQuestion size={28} strokeWidth={1.5} />}
      topSlot={<VpnDetectionToggle />}
    />
  )
}
